import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { connect } from 'react-redux';
import NoContent from 'commons/src/components/emptyStates/NoContent';
import { userIsHbs } from 'commons/src/components/findUserType';
import { fullwidthListElement, sensorPlaceholder } from 'commons/src/components/placeholder';
import { customPeriodName, dateFormats, mobileMax, sensorOrder } from 'commons/src/constants';
import { riskFactorSensors, virtualSensors as virtualSensorTypes } from 'commons/src/DeviceAndSensorLists';
import CurrentValuesTile from 'commons/src/features/devicePage/CurrentValuesTile';
import DeviceInfo from 'commons/src/features/devicePage/DeviceInfo';
import LindabSection from 'commons/src/features/devicePage/LindabTile';
import { DeviceTypeNames, SensorTypes } from 'commons/src/models/commonEnums';
import { FullDeviceData, SelectedPeriod } from 'commons/src/models/commonTypeScript';
import { Store } from 'commons/src/reducers';
import { CommonRequestType } from 'commons/src/reducers/requestReducer';
import OptionsRow from './OptionsRow';
import { redrawChart, zoomOut } from './SensorConfig';
import SensorTile from './SensorTile';

interface Sensor {
    type: SensorTypes;
    unit: string;
    deviceId: string;
    currentValue?: number;
    thresholds: number[];
}

type StateProps = {
    devicePageDevices: {
        [serialNumber: string]: FullDeviceData;
    };
    loadingVirtualSensorData: boolean;
};

export type ParentProps = {
    fetchCustomSegment: (
        serialNumber: string,
        selectedPeriod: SelectedPeriod,
        extraParams?: { [extraParam: string]: string | undefined }
    ) => void;
    updateSelectedPeriod: (selectedPeriod: SelectedPeriod) => void;
    endedSegment: boolean;
    devicePageLoading: boolean;
    fetchedDevices: { [serialNumber: string]: FullDeviceData };
    deviceId: string;
    dateFormat: string;
    device: FullDeviceData;
    selectedPeriod: SelectedPeriod;
    serialNumber: string;
    fetching: boolean;
    lastRecord?: string;
    onPollDeviceData?: (
        data: {
            serialNumber: string;
            selectedInterval: SelectedPeriod;
            fetching: boolean;
            loading: boolean;
        },
        withPolling?: boolean
    ) => void;
    stopPollingDeviceData?: () => void;
    macAddress?: string;
    timeZone?: string;
    withPolling?: boolean;
    showThresholdLines?: boolean;
    onShowThresholdLinesChange?: (show: boolean) => void;
    scaleGraphToThresholds?: boolean;
    onScaleGraphToThresholdsChange?: (value: boolean) => void;
    /** When false, hide the threshold legend in the left sensor card. */
    showLegendOnLeft?: boolean;
    onShowLegendOnLeftChange?: (value: boolean) => void;
    /** When set, overrides device.sensors[].thresholds with values from the thresholds API (same as settings). */
    apiThresholds?: Record<string, number[]>;
    /** Key to force sensor rows to remount (e.g. after reset layout). */
    sensorLayoutKey?: number;
    /** Callback to clear stored row heights and reset graph layout to default. */
    onResetSensorLayout?: () => void;
};

export type Props = ParentProps & StateProps;

export const DevicePageBodyComponent = (props: Props): React.ReactElement => {
    const {
        deviceId,
        selectedPeriod,
        fetchedDevices,
        fetchCustomSegment,
        updateSelectedPeriod,
        onPollDeviceData,
        stopPollingDeviceData,
        endedSegment,
        devicePageLoading,
        dateFormat,
        device,
        serialNumber,
        fetching,
        lastRecord,
        devicePageDevices,
        macAddress,
        timeZone,
        loadingVirtualSensorData,
        withPolling = true,
        showThresholdLines: showThresholdLinesProp,
        onShowThresholdLinesChange,
        scaleGraphToThresholds,
        onScaleGraphToThresholdsChange,
        showLegendOnLeft,
        onShowLegendOnLeftChange,
        apiThresholds,
        sensorLayoutKey,
        onResetSensorLayout,
    } = props;

    const [isExpanded, setIsExpanded] = useState(false);
    const [isZoomActive, setIsZoomActive] = useState(false);
    const [showThresholdLinesInternal, setShowThresholdLinesInternal] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars -- setter reserved when showThresholdLinesProp is undefined
    const [width, setWidth] = useState<number>(window.innerWidth);

    const handleResetLayout = useCallback((): void => {
        onResetSensorLayout?.();
        setIsExpanded(false);
    }, [onResetSensorLayout]);

    const showThresholdLines =
        showThresholdLinesProp !== undefined ? showThresholdLinesProp : showThresholdLinesInternal;

    const {
        segmentStart,
        segmentEnd,
        sensors,
        currentSensorValues,
        deviceType,
        relayDevice,
        isHubConnectionLost,
        virtualSensors,
    } = device;

    const hubSyncAndConnectionLost = relayDevice === DeviceTypeNames.hub && isHubConnectionLost;
    const { t: txt } = useTranslation();

    const onWindowSizeChange = (): void => {
        setWidth(window.innerWidth);
    };

    useEffect((): (() => void) => {
        window.addEventListener('resize', onWindowSizeChange);
        return (): void => {
            window.removeEventListener('resize', onWindowSizeChange);
        };
    }, []);

    // When the right-hand legend/details panel is toggled, the graph container width changes; redraw charts to fit.
    useEffect(() => {
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(redrawChart);
        });
        return () => cancelAnimationFrame(id);
    }, [showLegendOnLeft]);

    const isMobile = width <= mobileMax;

    const onResize = (e: SyntheticEvent<HTMLElement>): void => {
        e.preventDefault();
        setIsExpanded(!isExpanded);
        setIsZoomActive(false);
    };

    const selectGraphPeriod = (newSelectedPeriod: SelectedPeriod, isCustom?: boolean): void => {
        if (newSelectedPeriod === undefined) {
            throw Error('selecterPeriodUndefined');
        }

        const { startDate: prevStartDate, endDate: prevEndDate } = selectedPeriod;
        const { startDate, endDate } = newSelectedPeriod;

        const hasPeriod = fetchedDevices[deviceId] && fetchedDevices[deviceId].fetchedIntervals[newSelectedPeriod.name];
        const customPeriodHasChanged = startDate !== prevStartDate || endDate !== prevEndDate;

        if (isCustom && customPeriodHasChanged) {
            fetchCustomSegment(deviceId, newSelectedPeriod);
        }
        if (stopPollingDeviceData && onPollDeviceData) {
            if (isCustom && selectedPeriod.name !== customPeriodName) {
                stopPollingDeviceData();
            }
            if (!isCustom && selectedPeriod.name !== newSelectedPeriod.name) {
                stopPollingDeviceData();
                onPollDeviceData(
                    {
                        serialNumber: deviceId,
                        selectedInterval: newSelectedPeriod,
                        fetching: !hasPeriod,
                        loading: false,
                    },
                    withPolling
                );
            }
        }
        if (selectedPeriod.name !== newSelectedPeriod.name || isCustom) {
            updateSelectedPeriod(newSelectedPeriod);
            setIsZoomActive(false);
        }
    };

    const orderSensors = (sensorsToSort: Sensor[]): Sensor[] =>
        sensorsToSort.sort(
            (a, b) => sensorOrder.indexOf(a.type as SensorTypes) - sensorOrder.indexOf(b.type as SensorTypes)
        );

    const resetZoom = (): void => {
        zoomOut();
        setIsZoomActive(false);
    };

    const displayResetZoom = (): void => setIsZoomActive(true);

    const distributeSensors = (): { orderedSensors: Sensor[]; orderedVirtualSensors: Sensor[] } => {
        const groupSensors =
            (device &&
                device.childGroups &&
                device &&
                device.childGroups
                    .flatMap(childGroup => {
                        const deviceSensors = devicePageDevices[childGroup] && devicePageDevices[childGroup].sensors;
                        return deviceSensors && deviceSensors.map(sensor => ({ deviceId: childGroup, ...sensor }));
                    })
                    .filter(sensor => !!sensor)) ||
            [];

        const sensorsWithSerialNumber = [...(sensors || []).map(sensor => ({ ...sensor, deviceId })), ...groupSensors];
        const orderedSensors = sensorsWithSerialNumber
            .filter(sensor => !virtualSensorTypes.includes(sensor.type))
            .filter(sensor => !riskFactorSensors.includes(sensor.type))
            .filter(sensor => Object.keys(SensorTypes).includes(sensor.type));
        const virtualSensorsFromSamplesEndpoint = sensorsWithSerialNumber.filter(sensor =>
            virtualSensorTypes.includes(sensor.type)
        );

        if (userIsHbs()) {
            // if user us AfB replace radonShortTermAvg with hourlyRadon
            const hourlyRadon = sensorsWithSerialNumber.find(sensor => sensor.type === SensorTypes.hourlyRadon);
            const avgRadonIndex = orderedSensors.findIndex(sensor => sensor.type === SensorTypes.radonShortTermAvg);
            if (hourlyRadon && avgRadonIndex > -1) {
                orderedSensors[avgRadonIndex] = hourlyRadon;
            }
        }

        const orderedVirtualSensors = (virtualSensors || []).map(sensor => ({ ...sensor, deviceId }));
        const allVirtualSensors = [...virtualSensorsFromSamplesEndpoint, ...orderedVirtualSensors].filter(
            sensor => sensor.type !== SensorTypes.hourlyRadon // prevent showing radon hourly twice as it replaces radonShortTermAvg above
        );
        return { orderedSensors, orderedVirtualSensors: allVirtualSensors };
    };

    const renderRows = (sensorList: Sensor[]): React.ReactElement[] =>
        orderSensors(sensorList).map(sensor => {
            const thresholds =
                apiThresholds && apiThresholds[sensor.type]?.length ? apiThresholds[sensor.type] : sensor.thresholds;
            return (
                <SensorTile
                    showThresholdLines={showThresholdLines}
                    scaleGraphToThresholds={scaleGraphToThresholds}
                    showDetailsPanel={showLegendOnLeft}
                    timeZone={timeZone}
                    isExpanded={isExpanded}
                    isMobile={isMobile}
                    selectedInterval={selectedPeriod}
                    key={`sensor-tile-${serialNumber}-${sensor.type}`}
                    sensor={{ ...sensor, thresholds }}
                    deviceId={sensor.deviceId}
                    dateFormat={dateFormat}
                    displayStatusBar
                    displayResetZoom={displayResetZoom}
                />
            );
        });

    const { orderedSensors, orderedVirtualSensors } = distributeSensors();
    const displayGraphHeader = devicePageLoading || (sensors && sensors.length > 0);

    return (
        <div className="single-device">
            <div>
                <DeviceInfo
                    dateFormat={dateFormat as keyof typeof dateFormats}
                    lastRecord={lastRecord}
                    loading={devicePageLoading}
                    endedSegment={endedSegment}
                    device={device}
                    serialNumber={serialNumber}
                />
                {macAddress && <LindabSection macAddress={macAddress} />}
                {!endedSegment && (
                    <CurrentValuesTile
                        hubSyncAndConnectionLost={hubSyncAndConnectionLost}
                        serialNumber={serialNumber}
                        deviceType={deviceType}
                        currentSensorValues={currentSensorValues}
                    />
                )}
                {displayGraphHeader && (
                    <OptionsRow
                        selectGraphPeriod={selectGraphPeriod}
                        selectedPeriod={selectedPeriod}
                        onResize={onResize}
                        isExpanded={isExpanded}
                        isMobile={isMobile}
                        segmentStartDate={segmentStart}
                        segmentEndDate={segmentEnd}
                        dateFormat={dateFormat}
                        loading={devicePageLoading}
                        endedSegment={endedSegment}
                        zoomActive={isZoomActive}
                        resetZoom={resetZoom}
                        timeZone={timeZone}
                        onResetLayout={onResetSensorLayout ? handleResetLayout : undefined}
                        showThresholdLines={
                            onShowThresholdLinesChange
                                ? { value: showThresholdLines, onChange: onShowThresholdLinesChange }
                                : undefined
                        }
                        scaleGraphToThresholds={
                            onScaleGraphToThresholdsChange
                                ? { value: scaleGraphToThresholds ?? false, onChange: onScaleGraphToThresholdsChange }
                                : undefined
                        }
                        showLegendOnLeft={
                            onShowLegendOnLeftChange
                                ? { value: showLegendOnLeft ?? true, onChange: onShowLegendOnLeftChange }
                                : undefined
                        }
                    />
                )}
                <ReactPlaceholder
                    ready={!devicePageLoading && !fetching}
                    customPlaceholder={isMobile ? fullwidthListElement : sensorPlaceholder}
                >
                    {!devicePageLoading && device.sensors && device.sensors.length > 0 ? (
                        <React.Fragment key={`sensor-rows-${sensorLayoutKey ?? 0}`}>
                            {renderRows(orderedSensors)}
                            {orderedVirtualSensors.length > 0 && (
                                <>
                                    <h4 className="virtual-sensors-header">{txt('VirtualSensors')}</h4>
                                    <ReactPlaceholder
                                        ready={!loadingVirtualSensorData}
                                        customPlaceholder={isMobile ? fullwidthListElement : sensorPlaceholder}
                                    >
                                        {renderRows(orderedVirtualSensors)}
                                    </ReactPlaceholder>
                                </>
                            )}
                        </React.Fragment>
                    ) : (
                        <div className="page-wrapper">
                            <NoContent noContentText="AddDeviceUsingPhone" />
                        </div>
                    )}
                </ReactPlaceholder>
            </div>
        </div>
    );
};

const mapStateToProps = (store: Store): StateProps => {
    const {
        devicePage: { devices: devicePageDevices },
        commonRequests: {
            [CommonRequestType.FetchCustomSegmentVirtualSensors]: { loading: loadingCustomVirtualSensors },
            [CommonRequestType.PollVirtualDeviceSensorData]: { loading: loadingVirtualSensorData },
        },
    } = store;
    return {
        devicePageDevices,
        loadingVirtualSensorData: loadingCustomVirtualSensors || loadingVirtualSensorData,
    };
};

export default connect(mapStateToProps)(DevicePageBodyComponent);
